import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { masterSupabase } from '../lib/supabase-master';

// =====================================================
// 🔐 ENHANCED RBAC HOOK - PERFORMANCE OPTIMIZED
// =====================================================

// Cache für RBAC-Daten (5 Minuten)
const CACHE_DURATION = 5 * 60 * 1000; // 5 Minuten
let rbacCache = {
  data: null,
  timestamp: null,
  userId: null
};

const useRBAC = () => {
  const { user } = useAuth();
  const [userRoles, setUserRoles] = useState([]);
  const [userPermissions, setUserPermissions] = useState([]);
  const [rbacTags, setRbacTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const loadingRef = useRef(false); // Verhindert doppelte Abfragen

  // Load RBAC data on mount with caching
  useEffect(() => {
    if (user && !loadingRef.current) {
      loadRBACDataWithCache();
    } else if (!user) {
      // User ausgeloggt - Cache leeren und State zurücksetzen
      rbacCache = { data: null, timestamp: null, userId: null };
      setLoading(false);
      setUserRoles([]);
      setUserPermissions([]);
      setRbacTags([]);
    }
  }, [user]);

  // Load RBAC data with caching and parallel queries
  const loadRBACDataWithCache = async () => {
    try {
      // Prüfe Cache
      const now = Date.now();
      if (rbacCache.data && 
          rbacCache.userId === user.id && 
          rbacCache.timestamp && 
          (now - rbacCache.timestamp) < CACHE_DURATION) {
        
        console.log('🚀 RBAC: Using cached data');
        const { roles, permissions, tags } = rbacCache.data;
        setUserRoles(roles);
        setUserPermissions(permissions);
        setRbacTags(tags);
        setLoading(false);
        setError(null);
        return;
      }

      // Cache abgelaufen oder nicht vorhanden - neue Daten laden
      console.log('🔄 RBAC: Loading fresh data');
      loadingRef.current = true;
      setLoading(true);
      
      // PARALLEL QUERIES - Beide gleichzeitig starten
      const [rolesResponse, permissionsResponse] = await Promise.all([
        masterSupabase
          .from('user_roles')
          .select('*')
          .eq('user_id', user.id),
        
        // Alle Permissions laden (wird später gefiltert)
        masterSupabase
          .from('role_permissions')
          .select('*')
      ]);
      
      // Fehlerbehandlung
      if (rolesResponse.error) throw rolesResponse.error;
      if (permissionsResponse.error) throw permissionsResponse.error;
      
      const rolesData = rolesResponse.data || [];
      const allPermissions = permissionsResponse.data || [];
      
      // Permissions für User-Rollen filtern
      const roleIds = rolesData.map(r => r.role);
      const userPermissions = allPermissions.filter(p => roleIds.includes(p.role));
      
      // Generate RBAC tags (Original-Logik beibehalten)
      const tags = generateRBACTags(rolesData);
      
      // Cache aktualisieren
      rbacCache = {
        data: {
          roles: rolesData,
          permissions: userPermissions,
          tags: tags
        },
        timestamp: now,
        userId: user.id
      };
      
      // State setzen
      setUserRoles(rolesData);
      setUserPermissions(userPermissions);
      setRbacTags(tags);
      setError(null);
      
      console.log('✅ RBAC: Data loaded and cached');
      
    } catch (err) {
      console.error('❌ RBAC Error:', err);
      setError(err.message);
      
      // Fallback zu sicheren Defaults (Original-Verhalten)
      setUserRoles([]);
      setUserPermissions([]);
      setRbacTags(['isClient']); // Sicherster Default
      
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  // Generate RBAC tags (Original-Logik unverändert)
  const generateRBACTags = (rolesData) => {
    const tags = [];
    console.log('🔍 RBAC DEBUG: rolesData =', rolesData);
    
    if (rolesData.some(r => r.role === 'SUPER_ADMIN')) {
      console.log('✅ RBAC: User has SUPER_ADMIN role');
      tags.push('isSuperAdmin', 'hasFullAccess', 'canUseClaraKI', 'canManageProperties');
    } else if (rolesData.some(r => r.role === 'ADMIN')) {
      console.log('✅ RBAC: User has ADMIN role');
      tags.push('isAdmin', 'hasFullAccess', 'canUseClaraKI', 'canManageProperties');
    } else if (rolesData.some(r => r.role === 'MANAGER')) {
      console.log('✅ RBAC: User has MANAGER role');
      tags.push('isManager', 'canEditOwn', 'canUseClaraKI', 'canManageProperties');
    } else if (rolesData.some(r => r.role === 'CLIENT')) {
      console.log('✅ RBAC: User has CLIENT role');
      tags.push('isClient', 'canEditOwn', 'canUseClaraKI');
    } else {
      console.log('❌ RBAC: No recognized role found');
      tags.push('isClient'); // Sicherer Default
    }
    
    return tags;
  };

  // Helper functions (Original-Logik beibehalten)
  const hasRole = (role) => {
    return userRoles.some(r => r.role === role);
  };

  const hasPermission = (permission) => {
    return userPermissions.some(p => p.permission === permission);
  };

  const hasTag = (tag) => {
    return rbacTags.includes(tag);
  };

  const canAccess = (resource, action = 'read') => {
    // Super Admin kann alles
    if (hasTag('isSuperAdmin')) return true;
    
    // Admin kann fast alles
    if (hasTag('isAdmin')) return true;
    
    // Manager kann eigene Daten bearbeiten
    if (hasTag('isManager') && action === 'read') return true;
    if (hasTag('isManager') && action === 'edit' && hasTag('canEditOwn')) return true;
    
    // Client kann nur lesen
    if (hasTag('isClient') && action === 'read') return true;
    
    return false;
  };

  // Cache manuell leeren (für Debugging)
  const clearCache = () => {
    rbacCache = { data: null, timestamp: null, userId: null };
    console.log('🗑️ RBAC: Cache cleared');
  };

  return {
    userRoles,
    userPermissions,
    rbacTags,
    loading,
    error,
    hasRole,
    hasPermission,
    hasTag,
    canAccess,
    clearCache, // Für Debugging
    
    // Convenience methods (Original beibehalten)
    isSuperAdmin: hasTag('isSuperAdmin'),
    isAdmin: hasTag('isAdmin'),
    isManager: hasTag('isManager'),
    isClient: hasTag('isClient'),
    hasFullAccess: hasTag('hasFullAccess'),
    canUseClaraKI: hasTag('canUseClaraKI'),
    canManageProperties: hasTag('canManageProperties'),
    canEditOwn: hasTag('canEditOwn')
  };
};

export default useRBAC;

